import { useCallback } from "react";
import { BarChart3, Book, BookOpen, FileDigit, Loader2, Layers } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { CatalogReportDateFilters } from "@/components/catalog/CatalogReportDateFilters";
import { EmptyState } from "@/components/ui/empty-state";
import { StatCard } from "@/components/ui/stat-card";
import { catalogReportsApi } from "@/lib/api/catalog-reports";
import { formatNumberWithCommas } from "@/lib/formatters";
import { useCatalogReportLoader, useCatalogReportQuery } from "@/hooks/useCatalogReportQuery";

export default function MyCatalogStatsPage() {
  const query = useCatalogReportQuery("thisMonth");

  const loadStats = useCallback(
    (range: { from?: string; to?: string }) => catalogReportsApi.getMyStats(range),
    []
  );

  const { data, loading } = useCatalogReportLoader(
    loadStats,
    query.range,
    query.isCustomRangeReady,
    "Unable to load your catalog stats."
  );

  const stats = data?.[0] ?? { book: 0, journal: 0, eResource: 0, total: 0 };

  return (
    <div className="space-y-6">
      <PageHeader
        title="My Catalog Stats"
        description="Your cataloging activity for the selected period."
        showSearch={false}
      />

      <CatalogReportDateFilters
        preset={query.preset}
        customFrom={query.customFrom}
        customTo={query.customTo}
        onPresetChange={query.setPreset}
        onCustomFromChange={query.setCustomFrom}
        onCustomToChange={query.setCustomTo}
      />

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      ) : stats.total === 0 ? (
        <EmptyState
          icon={<BarChart3 className="h-6 w-6 text-slate-400" />}
          title="No cataloging activity yet"
          description="Items you catalog in the selected period will appear here."
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard
            icon={<Book className="w-5 h-5" />}
            label="Books cataloged"
            value={formatNumberWithCommas(stats.book)}
            iconBg="bg-blue-50 text-blue-700"
          />
          <StatCard
            icon={<BookOpen className="w-5 h-5" />}
            label="Journals cataloged"
            value={formatNumberWithCommas(stats.journal)}
            iconBg="bg-violet-50 text-violet-700"
          />
          <StatCard
            icon={<FileDigit className="w-5 h-5" />}
            label="E-resources cataloged"
            value={formatNumberWithCommas(stats.eResource)}
            iconBg="bg-emerald-50 text-emerald-700"
          />
          <StatCard
            icon={<Layers className="w-5 h-5" />}
            label="Total items"
            value={formatNumberWithCommas(stats.total)}
            iconBg="bg-amber-50 text-amber-700"
          />
        </div>
      )}
    </div>
  );
}
